import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { JSX, ReactNode } from 'react'

import type { WriteQueue } from './queue.js'
import { type EnqueueRequest } from './queue.js'
import type { QueueSnapshot } from './types.js'

/**
 * The queue, as the only write path a component can reach. §6.22 `OFF-001a`, `OFF-005`.
 *
 * A component gets `enqueue` and a snapshot. It does not get a transport, a base URL or a token,
 * because `OFF-001a` says "no direct API calls from the UI" and the surest way to honour that is
 * for the UI to have nothing to call with.
 */

interface WriteQueueValue {
  readonly enqueue: (request: EnqueueRequest) => Promise<unknown>
  readonly snapshot: QueueSnapshot
  /** Send a blocked write again, having fixed what refused it. */
  readonly retry: (id: string) => Promise<void>
  /** Throw a blocked write away. Loses whatever it carried, so it asks first at the call site. */
  readonly discard: (id: string) => Promise<void>
  /** Keep this write's change against the version the record is now at. A version conflict only. */
  readonly resolve: (id: string, currentVersion: number) => Promise<void>
}

const Context = createContext<WriteQueueValue | null>(null)

export function WriteQueueProvider({
  queue,
  children,
}: {
  readonly queue: WriteQueue
  readonly children: ReactNode
}): JSX.Element {
  const [snapshot, setSnapshot] = useState<QueueSnapshot>(() => queue.snapshot())

  useEffect(() => {
    let cancelled = false
    const sync = (): void => {
      if (!cancelled) setSnapshot(queue.snapshot())
    }

    const online = (): void => {
      queue.setOnline(true)
      void queue.drain().then(sync)
    }
    const offline = (): void => {
      queue.setOnline(false)
      sync()
    }

    window.addEventListener('online', online)
    window.addEventListener('offline', offline)
    queue.setOnline(navigator.onLine)

    void (queue.loaded ? Promise.resolve() : queue.load()).then(() => queue.drain()).then(sync)

    // A tick rather than a socket: OFF-001's failure is a thirty-second outage, and a five-second
    // retry cycle covers it without a connection to keep alive.
    const timer = setInterval(() => {
      void queue.drain().then(sync)
    }, 5_000)

    return () => {
      cancelled = true
      clearInterval(timer)
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offline)
    }
  }, [queue])

  const value = useMemo<WriteQueueValue>(
    () => ({
      enqueue: async (request) => {
        const write = await queue.enqueue(request)
        setSnapshot(queue.snapshot())
        void queue.drain().then(() => setSnapshot(queue.snapshot()))
        return write
      },
      snapshot,
      retry: async (id) => {
        await queue.retry(id)
        setSnapshot(queue.snapshot())
        await queue.drain()
        setSnapshot(queue.snapshot())
      },
      resolve: async (id, currentVersion) => {
        await queue.resolve(id, currentVersion)
        setSnapshot(queue.snapshot())
        // The conflict was the head of the queue, so everything behind it has been waiting. Drain
        // now rather than leaving the shop until the next five-second tick.
        await queue.drain()
        setSnapshot(queue.snapshot())
      },
      discard: async (id) => {
        await queue.discard(id)
        setSnapshot(queue.snapshot())
        // Discarding the head unblocks everything queued behind it, so drain immediately rather
        // than leaving the shop waiting for the next five-second tick.
        await queue.drain()
        setSnapshot(queue.snapshot())
      },
    }),
    [queue, snapshot],
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export function useWriteQueue(): WriteQueueValue {
  const value = useContext(Context)
  if (!value) {
    // Not a soft fallback. A component that reached this without a provider would otherwise write
    // nowhere and look like it worked, which is the exact failure OFF-005 says staff must never
    // be exposed to.
    throw new Error('useWriteQueue was called outside a WriteQueueProvider')
  }
  return value
}
